import React, {useState} from 'react';
import {
    Show,
    useRecordContext,
    useRedirect,
    useTranslate,
    usePermissions,
} from 'react-admin';
import {Avatar, Box, Link as MuiLink, Typography, Alert} from '@mui/material';
import {Link as RouterLink} from 'react-router-dom';
import LoadingButton from '@mui/lab/LoadingButton';
import {EditOutlined, DeleteOutline, OpenInNew} from '@mui/icons-material';
import {t} from '../themes/statamicTokens';
import PageHeader from '../components/statamic/PageHeader';
import PrimaryButton from '../components/statamic/PrimaryButton';
import SectionCard from '../components/statamic/SectionCard';
import DataRow from '../components/statamic/DataRow';
import StatusDot from '../components/statamic/StatusDot';
import StatusPill from '../components/statamic/StatusPill';
import DeleteConfirmDialog from '../components/statamic/DeleteConfirmDialog';
import useDeleteConfirm from '../components/statamic/useDeleteConfirm';
import {axiosApiInstance} from '../AxiosUtils';
import hasPermission from '../auth/hasPermission';
import DynamicMetadataDisplay from '../components/DynamicMetadataDisplay';

const formatDate = (v) => {
    if (!v) return null;
    const d = new Date(v); return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString();
};

const formatDateTime = (v) => {
    if (!v) return null;
    const d = new Date(v); return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
};

const paymentStatusVariant = (status) => {
    switch (status) {
        case 'active':    return 'success';
        case 'pending':   return 'warning';
        case 'paused':    return 'neutral';
        case 'failed':    return 'danger';
        case 'cancelled': return 'danger';
        default:          return 'neutral';
    }
};

const MetaRow = ({label, children}) => (
    <Box sx={{display: 'grid', gridTemplateColumns: '140px 1fr', alignItems: 'baseline', gap: 1.5, py: 0.5}}>
        <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.secondary, fontWeight: 500}}>
            {label}
        </Typography>
        <Box sx={{fontSize: `${t.type.body.size}px`, color: t.color.text.primary, minWidth: 0}}>
            {children || <Box component="span" sx={{color: t.color.text.muted}}>—</Box>}
        </Box>
    </Box>
);

const GuardianHero = ({record, translate}) => {
    const avatarUrl = record.avatar ? `${process.env.REACT_APP_CDN_URL}/${record.avatar}` : undefined;
    const salutation = record.salutation ? translate(record.salutation) : '';
    const fullName = [salutation, record.given_name, record.family_name].filter(Boolean).join(' ') || 'Unnamed';
    const sponsorCount = record.children?.length ?? 0;

    return (
        <Box
            sx={{
                backgroundColor: t.color.surface,
                border: `1px solid ${t.color.border.default}`,
                borderRadius: `${t.radius.md}px`,
                p: 3,
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 4,
                alignItems: 'stretch',
            }}
        >
            <Box sx={{display: 'flex', gap: 3, alignItems: 'center', minWidth: 0}}>
                <Avatar
                    src={avatarUrl}
                    sx={{
                        width: 96,
                        height: 96,
                        fontSize: 32,
                        flexShrink: 0,
                        bgcolor: t.color.sidebarHover,
                        color: t.color.text.secondary,
                        border: `1px solid ${t.color.border.hairline}`,
                    }}
                >
                    {(record.given_name?.charAt(0) ?? '') + (record.family_name?.charAt(0) ?? '')}
                </Avatar>
                <Box sx={{minWidth: 0, flex: 1}}>
                    <Typography
                        sx={{
                            fontSize: 22,
                            fontWeight: 700,
                            color: t.color.text.primary,
                            lineHeight: 1.2,
                            wordBreak: 'break-word',
                        }}
                    >
                        {fullName}
                    </Typography>
                    {record.relation_number && (
                        <Typography sx={{mt: 0.5, fontSize: `${t.type.small.size}px`, color: t.color.text.secondary}}>
                            {translate('Relation number')} #{record.relation_number}
                        </Typography>
                    )}
                    <Box sx={{display: 'flex', gap: 0.75, mt: 1.5, flexWrap: 'wrap'}}>
                        <StatusPill variant={record.enabled ? 'success' : 'neutral'}>
                            {record.enabled ? translate('Enabled') : translate('Disabled')}
                        </StatusPill>
                        {sponsorCount > 0 && (
                            <StatusPill variant="info">
                                {sponsorCount === 1 ? 'Sponsor of 1 child' : `Sponsor of ${sponsorCount} children`}
                            </StatusPill>
                        )}
                        {record.sponsor_as_company && (
                            <StatusPill variant="neutral">Company</StatusPill>
                        )}
                        {record.payment_status && (
                            <StatusPill variant={paymentStatusVariant(record.payment_status)}>
                                {translate(record.payment_status) || record.payment_status}
                            </StatusPill>
                        )}
                    </Box>
                </Box>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    pl: 3,
                    borderLeft: `1px solid ${t.color.border.hairline}`,
                }}
            >
                <MetaRow label={translate('Email')}>
                    {record.email ? (
                        <MuiLink
                            href={`mailto:${record.email}`}
                            sx={{color: t.color.accent.primary, textDecoration: 'none', '&:hover': {textDecoration: 'underline'}}}
                        >
                            {record.email}
                        </MuiLink>
                    ) : null}
                </MetaRow>
                <MetaRow label={translate('Phone')}>
                    {record.phone}
                </MetaRow>
                <MetaRow label={translate('Registration date')}>
                    {formatDate(record.create_date_time)}
                </MetaRow>
                <MetaRow label={translate('Last access date')}>
                    {formatDateTime(record.last_access_date_time)}
                </MetaRow>
                {record.scope_charity_name && (
                    <MetaRow label={translate('Organisation')}>
                        {record.scope_charity_name}
                    </MetaRow>
                )}
            </Box>
        </Box>
    );
};

const ChildCard = ({child, translate}) => (
    <Box
        component={RouterLink}
        to={`/children/${child.external_id}/show`}
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.5,
            borderRadius: `${t.radius.md}px`,
            border: `1px solid ${t.color.border.default}`,
            textDecoration: 'none',
            color: 'inherit',
            transition: 'border-color 120ms ease, background-color 120ms ease',
            '&:hover': {
                borderColor: t.color.border.strong,
                backgroundColor: t.color.sidebarHover,
            },
        }}
    >
        <Avatar
            src={child.profile_image_thumbnail
                ? `${process.env.REACT_APP_CDN_URL}/${child.profile_image_thumbnail}`
                : undefined}
            sx={{
                width: 40,
                height: 40,
                bgcolor: t.color.sidebarHover,
                color: t.color.text.secondary,
                border: `1px solid ${t.color.border.hairline}`,
            }}
        />
        <Box sx={{minWidth: 0, flex: 1}}>
            <Typography
                sx={{
                    fontSize: `${t.type.bodyStrong.size}px`,
                    fontWeight: t.type.bodyStrong.weight,
                    color: t.color.text.primary,
                }}
            >
                {child.first_name} {child.last_name}
            </Typography>
            <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.secondary}}>
                {translate('Sponsored child')}
            </Typography>
        </Box>
    </Box>
);

const ResetPasswordAction = ({record, permissions, translate}) => {
    const [resetting, setResetting] = useState(false);
    const [status, setStatus] = useState(null);

    const resetPassword = () => {
        setResetting(true);
        setStatus(null);
        const rolePath = permissions === 'admin' ? 'admin' : 'umbrellad';
        axiosApiInstance.post(
            `${process.env.REACT_APP_API_URL}/api/v2/${rolePath}/guardians/${record.id}/resetPassword`
        ).then(() => {
            setResetting(false);
            setStatus('success');
        }).catch(() => {
            setResetting(false);
            setStatus('error');
        });
    };

    return (
        <Box>
            <LoadingButton
                onClick={resetPassword}
                loading={resetting}
                variant="outlined"
                size="small"
                sx={{
                    textTransform: 'none',
                    fontSize: `${t.type.small.size}px`,
                    borderColor: t.color.border.default,
                    color: t.color.text.primary,
                    borderRadius: `${t.radius.sm}px`,
                    '&:hover': { borderColor: t.color.accent.primary, color: t.color.accent.primary, backgroundColor: 'transparent' },
                }}
            >
                {translate('Reset password')}
            </LoadingButton>
            {status === 'success' && (
                <Alert severity="success" sx={{mt: 1, fontSize: `${t.type.small.size}px`}}>
                    {translate('Password reset mail sent')}
                </Alert>
            )}
            {status === 'error' && (
                <Alert severity="warning" sx={{mt: 1, fontSize: `${t.type.small.size}px`}}>
                    {translate('Could not send password reset mail')}
                </Alert>
            )}
        </Box>
    );
};

const GuardianShowContent = () => {
    const record = useRecordContext();
    const redirect = useRedirect();
    const translate = useTranslate();
    const {permissions} = usePermissions();
    const deleteConfirm = useDeleteConfirm();

    if (!record) return null;

    const canExportDetails = hasPermission(permissions, ['export_details'], 'guardians');
    // Charity roles may only list and show sponsors; they have no edit route.
    const canEdit = hasPermission(permissions, ['edit'], 'guardians');
    // Plan §2.3/§3 (D5): sponsored_child_count_in_scope === 0 marks a past sponsor —
    // read-only until an attach gives them a current child again (PUT/resetPassword
    // 404 server-side). UMBRELLA_ADMIN-only field, absent for NOFAM_ADMIN, so this
    // never hides Edit/Reset password for an admin.
    const isPastSponsor = record.sponsored_child_count_in_scope === 0;
    const fullName = [
        record.salutation ? translate(record.salutation) : null,
        record.given_name,
        record.family_name,
    ].filter(Boolean).join(' ') || 'Unnamed';

    return (
        <Box sx={{backgroundColor: t.color.page, minHeight: '100%'}}>
            <PageHeader
                breadcrumb={{label: translate('Sponsors'), to: '/guardians'}}
                title={
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <StatusDot variant={record.enabled ? 'success' : 'neutral'}/>
                        <Box component="span">{fullName}</Box>
                    </Box>
                }
                actions={
                    <>
                        {canEdit && !isPastSponsor && (
                            <PrimaryButton
                                variant="primary"
                                startIcon={<EditOutlined sx={{fontSize: 16}}/>}
                                onClick={() => redirect('edit', 'guardians', record.id)}
                            >
                                {translate('Edit')}
                            </PrimaryButton>
                        )}
                        {permissions === 'admin' && (
                            <PrimaryButton
                                variant="danger"
                                startIcon={<DeleteOutline sx={{fontSize: 16}}/>}
                                onClick={deleteConfirm.openDialog}
                            >
                                {translate('Delete')}
                            </PrimaryButton>
                        )}
                    </>
                }
            />

            <Box sx={{p: 3, display: 'flex', flexDirection: 'column', gap: 2}}>
                <GuardianHero record={record} translate={translate}/>

                {record.children?.length > 0 && (
                    <SectionCard
                        title={translate('Guardian of')}
                        subtitle={`${record.children.length} child${record.children.length === 1 ? '' : 'ren'}`}
                        actions={
                            <MuiLink
                                component={RouterLink}
                                to={`/posts?filter=${encodeURIComponent(JSON.stringify({user_id: record.id}))}`}
                                sx={{
                                    fontSize: `${t.type.small.size}px`,
                                    color: t.color.accent.primary,
                                    textDecoration: 'none',
                                    '&:hover': {textDecoration: 'underline'},
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                }}
                            >
                                {translate('View posts')}
                                <OpenInNew sx={{fontSize: 13}}/>
                            </MuiLink>
                        }
                    >
                        <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 1}}>
                            {record.children.map(c => <ChildCard key={c.external_id} child={c} translate={translate}/>)}
                        </Box>
                    </SectionCard>
                )}

                {canExportDetails && (
                    <SectionCard
                        title={translate('Contact details')}
                        actions={!isPastSponsor && (
                            <ResetPasswordAction record={record} permissions={permissions} translate={translate}/>
                        )}
                    >
                        {isPastSponsor && (
                            <Alert severity="info" sx={{mb: 1.5, fontSize: `${t.type.small.size}px`}}>
                                {translate('Sponsorship in this umbrella has ended')} — {translate('Editing is unavailable until this sponsor has a child in your umbrella again')}
                            </Alert>
                        )}
                        <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 3, rowGap: 0}}>
                            <DataRow label={translate('Email')} value={record.email}/>
                            <DataRow label={translate('Phone')} value={record.phone}/>
                            <DataRow label={translate('Notification registration status')} value={record.firebase_status}/>
                        </Box>
                    </SectionCard>
                )}

                {canExportDetails && (
                    <SectionCard title={translate('Address')}>
                        <Box sx={{display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', columnGap: 3, rowGap: 0}}>
                            <DataRow label={translate('Street')} value={record.street}/>
                            <DataRow label={translate('House number')} value={record.house_number}/>
                            <DataRow label={translate('Postal code')} value={record.postal_code}/>
                        </Box>
                        <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 3, rowGap: 0, mt: 1}}>
                            <DataRow label={translate('City')} value={record.city}/>
                            <DataRow label={translate('Country')} value={record.country}/>
                        </Box>
                    </SectionCard>
                )}

                {!canExportDetails && (
                    <SectionCard title={translate('Location')}>
                        <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 3, rowGap: 0}}>
                            <DataRow label={translate('City')} value={record.city}/>
                            <DataRow label={translate('Country')} value={record.country}/>
                        </Box>
                    </SectionCard>
                )}

                {canExportDetails && (
                    <SectionCard title={translate('Payment information')}>
                        <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 3, rowGap: 0}}>
                            <DataRow label={translate('Payment method')}
                                     value={record.donation_method ? String(record.donation_method).toUpperCase() : null}/>
                            <DataRow label={translate('Sponsor as company')}>
                                <Box sx={{display: 'inline-flex', alignItems: 'center', gap: 0.75}}>
                                    <StatusDot variant={record.sponsor_as_company ? 'success' : 'neutral'}/>
                                    <Typography sx={{fontSize: `${t.type.body.size}px`, color: t.color.text.primary}}>
                                        {record.sponsor_as_company ? translate('Yes') : translate('No')}
                                    </Typography>
                                </Box>
                            </DataRow>
                            {record.sponsor_as_company && record.company && (
                                <DataRow label={translate('Company')} value={record.company}/>
                            )}
                            {record.donation_method === 'iban' && (
                                <>
                                    <DataRow label={translate('IBAN')} value={record.iban}/>
                                    <DataRow label={translate('Direct debit start date')}
                                             value={formatDate(record.direct_debit_start_date)}/>
                                    <DataRow label={translate('Direct debit reference')}
                                             value={record.direct_debit_reference}/>
                                </>
                            )}
                            {record.donation_method === 'stripe' && (
                                <>
                                    <DataRow label={translate('Stripe Subscription ID')} value={record.stripe_subscription_id}/>
                                    <DataRow label={translate('Stripe Customer ID')} value={record.stripe_customer_id}/>
                                </>
                            )}
                        </Box>
                        <Box sx={{mt: 1, pt: 1, borderTop: `1px solid ${t.color.border.hairline}`, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', columnGap: 3, rowGap: 0}}>
                            <DataRow label={translate('Payment Status')}>
                                {record.payment_status ? (
                                    <StatusPill variant={paymentStatusVariant(record.payment_status)}>
                                        {translate(record.payment_status) || record.payment_status}
                                    </StatusPill>
                                ) : null}
                            </DataRow>
                            <DataRow label={translate('Payment Status Date')}
                                     value={formatDateTime(record.payment_status_date)}/>
                            <DataRow label={translate('Last Payment Date')}
                                     value={formatDateTime(record.last_payment_date)}/>
                            <DataRow label={translate('Next Payment Date')}
                                     value={formatDate(record.next_payment_date)}/>
                        </Box>
                        {record.payment_status_reason && (
                            <Box sx={{mt: 1, pt: 1, borderTop: `1px solid ${t.color.border.hairline}`}}>
                                <DataRow label={translate('Payment Status Reason')} value={record.payment_status_reason}/>
                            </Box>
                        )}
                    </SectionCard>
                )}

                {canExportDetails && record.note && (
                    <SectionCard title={translate('Note')}>
                        <Typography sx={{fontSize: `${t.type.body.size}px`, color: t.color.text.primary, whiteSpace: 'pre-wrap'}}>
                            {record.note}
                        </Typography>
                    </SectionCard>
                )}

                <DynamicMetadataDisplay entityType="GUARDIAN"/>
            </Box>

            <DeleteConfirmDialog
                open={deleteConfirm.open}
                onClose={deleteConfirm.closeDialog}
                resource="guardians"
                record={record}
                recordLabel={fullName}
            />
        </Box>
    );
};

const GuardianShowStatamic = (props) => (
    <Show {...props} actions={false} component={Box} sx={{backgroundColor: t.color.page}}>
        <GuardianShowContent/>
    </Show>
);

export default GuardianShowStatamic;
